import { cx } from "@suse-o11y/class-variance";
import type { Time } from "@suse-o11y/date-time/time";
import { Dialog, DialogBoundary, DialogClose, DialogTrigger, useDialogClose } from "@suse-o11y/dialog";
import { IconArrowDownRounded, IconClose, IconLeftOutlined, IconMuted } from "@suse-o11y/icons";
import { LocalStorage, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR } from "@suse-o11y/local-storage";
import { isNonEmptyArray } from "@suse-o11y/non-empty-array";
import { Signal, batch, useMonitor } from "@suse-o11y/signal";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import { useEffect } from "@suse-o11y/use-effect";
import { usePermanent } from "@suse-o11y/use-permanent";
import React, { useState } from "react";
import { z } from "zod";

import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import type { NodeId } from "~/models/dto";
import { Permission } from "~/models/dto";

import type { TimeRange } from "../../../../../app/state/view/timeline/TimeRange";
import { isHealthStateUnHealthy } from "../../../../../common/functions";
import type { MonitorCheckStatus } from "../../../../../common/openapi";
import { usePermissions } from "../../../../../common/use-permissions";
import { ComponentBoundary } from "../../../../../components/_component-boundary";
import { DocLink } from "../../../../../components/react/docLink/DocLink";
import { LinkTo } from "../../../../../components/react/link";
import { LoadingSpinner } from "../../../../../components/react/loading-spinner/LoadingSpinner";
import { NotFound } from "../../../../../components/react/not-found/NotFound";
import { StateComponent } from "../../../../../components/react/states/StateComponent";
import type { SimpleStatefulComponent } from "../../../../../components/react/states/state-component-utils";
import { queryMonitorCheckStatus } from "../../../../../queries";
import { useQuery } from "../../../../../queries/react-query";
import { useRefetchQueriesInNamespace } from "../../../../../queries/use-refetch-queries-in-namespace";
import { COMPONENT_PATH } from "../../../../../routes/Routes";
import { usePinnedItemsState } from "../../../panes/east/pinned-items";
import type { PinnedItemsState } from "../../../panes/east/pinned-items/PinnedItemsState";
import { PinningButton } from "../../../panes/east/pinned-items/PinningButton";
import { MONITOR_STATUS_DRAWER_QUERY_NAMESPACE } from "../_query-namespace";
import { MonitorHealthTimeline } from "../MonitorHealthTimeline";
import { MonitorIntro } from "../MonitorIntro";
import { MonitorRemediationTabs } from "../MonitorRemediationTabs";
import { MonitorStatusDrawerHealthState } from "../MonitorStatusDrawerHealthState";
import { MonitorStatusDrawerTabs } from "../MonitorStatusDrawerTabs";

import { MonitorStatusDrawerProvider } from "./monitor-status-drawer-provider";
import { CheckStateChain } from "./monitor-status-drawer-state";
import { MuteMonitorDrawer } from "./mute-monitor-drawer";
import { SILENCING_DOC_LINK_SLUG } from "./silencing-doc-link-slug";
import { UnmuteMonitorDrawer } from "./unmute-monitor-drawer";

interface MonitorStatusDrawerProps {
	monitorStatusIdentifier: string;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const MonitorStatusDrawer: React.FC<MonitorStatusDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide" data-beest-testid="monitor-status-drawer">
		<MonitorStatusDrawerContent {...props} />
	</Dialog>
);

const MonitorStatusDrawerContent: React.FC<MonitorStatusDrawerProps> = ({
	monitorStatusIdentifier,
	timestamp,
	timeRange,
}) => {
	const permissions = usePermissions();
	const [, refetchQueries] = useRefetchQueriesInNamespace(MONITOR_STATUS_DRAWER_QUERY_NAMESPACE);
	const monitor = useMonitor();
	const pinnedItemsState = usePinnedItemsState();
	const scrollRef = React.useRef<HTMLDivElement>(null);

	const [state, timestampSignal, timeRangeSignal, refreshIntervalSignal] = usePermanent(() => {
		const timestampSource = Signal(timestamp ?? Date.now());
		const timeRangeSource = Signal(timeRange);

		return [
			CheckStateChain.init({
				identifier: monitorStatusIdentifier,
				selectedTab: Signal(),
				showImpactedComponent: Signal(false),
			}),
			Signal(
				(getterMonitor) => timestampSource.read(getterMonitor),
				(newTimestamp) => {
					timestampSource.write(newTimestamp);
					timeRangeSource.write((currentTimeRange) => currentTimeRange.toSpecific());
				},
			),
			Signal(timeRangeSource, (newTimeRange) => {
				timeRangeSource.write(newTimeRange);
				if (!newTimeRange.isSpecific()) {
					timestampSource.write(Date.now());
				}
			}),
			Signal<RefreshInterval>("off"),
		];
	});

	const checkState = state.peek(monitor);

	const [cachedData, setCachedData] = useState<MonitorCheckStatus>();
	const {
		data = cachedData?.identifier === checkState?.identifier ? cachedData : undefined,
		isLoading,
		error,
	} = useQuery({
		keepPreviousData: cachedData?.identifier === checkState?.identifier,
		enabled: checkState != null,
		...queryMonitorCheckStatus({
			identifier: checkState?.identifier ?? "",
			timestamp: timestampSignal.read(monitor),
		}),
	});

	useEffect(() => {
		if (data != null) {
			setCachedData(data);
		}
	}, [data]);

	const component: undefined | SimpleStatefulComponent = data && {
		id: data.component.id as NodeId,
		name: data.component.name,
		identifiers: [data.component.identifier],
		typeName: data.component.type,
		icon: data.component.icon,
		healthState: data.health,
	};

	React.useEffect(() => {
		if (checkState != null) {
			scrollRef.current?.scrollTo(0, 0);
		}
	}, [checkState]);

	const scrollDownIndicatorVisible =
		LocalStorage.getItem(monitor, MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, z.boolean()) ?? true;

	const showScrollIndicator =
		scrollDownIndicatorVisible &&
		scrollRef.current != null &&
		scrollRef.current.clientHeight < scrollRef.current.scrollHeight;

	const refreshTimeAndQueries = () => {
		batch((batchMonitor) => {
			const currentTimeRange = timeRangeSignal.read(batchMonitor);
			if (!currentTimeRange.isSpecific()) {
				timestampSignal.write(Date.now());
				timeRangeSignal.write(currentTimeRange);
			}
		});
		refetchQueries();
	};

	const showSilencingRules = permissions?.hasSystemPermission(Permission.GetSilencingRules);

	return (
		<>
			<header className="drawer-header" data-beest-testid="monitor-status-drawer-header">
				<StateComponent
					className="mr-auto"
					component={component}
					displayName
					displayTypeName
					typeOfData="Monitor status"
				/>

				{state.size(monitor) > 1 && (
					<button className="button button-tertiary gap-2" onClick={() => state.pop()} type="button">
						<IconLeftOutlined className="text-xs" />
						Previous
					</button>
				)}

				{error == null && <PinningButton item={data == null ? undefined : { ...data, timeRange: timeRangeSignal }} />}

				<DialogClose aria-label="Close" className="button button-square">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section
				className="drawer-body"
				onScroll={() => {
					LocalStorage.setItem(MONITOR_STATUS_DRAWER_SCROLL_INDICATOR, false);
				}}
				ref={scrollRef}
			>
				<ComponentBoundary component={data?.component}>
					<MonitorStatusDrawerProvider timeRange={timeRangeSignal} timestamp={timestampSignal}>
						{data && <MonitorIntro monitorStatus={data} />}
						{!isLoading && checkState && (
							<MonitorHealthTimeline
								action={
									error == null &&
									data != null &&
									showSilencingRules && (
										<MuteButton
											data={data}
											isLiveMode={!timeRangeSignal.read(monitor).isSpecific()}
											refresh={refreshTimeAndQueries}
										/>
									)
								}
								disabled={isLoading}
								identifier={checkState.identifier}
								onRefresh={refreshTimeAndQueries}
								refreshIntervalSignal={refreshIntervalSignal}
							/>
						)}

						{isLoading && <LoadingSpinner className="h-full" size="large" />}

						{error != null && (
							<NotFound header="No data found">
								Either the component or the observed health state does not exist at the selected time.
								<br />
								Choose a different time by clicking the monitor health timeline in a non-grey area.
							</NotFound>
						)}

						{!(isLoading || error) && data && (
							<>
								<MonitorStatusDrawerHealthState
									health={data.health}
									reason={data.reason}
									selectedTimestamp={data.topologyTime}
									silencing={showSilencingRules ? data.silenced : []}
									triggeredTimeStamp={data.triggeredTimestamp}
								/>

								<div>
									<MonitorStatusDrawerTabs checkState={data} checkStateChain={state} />
								</div>

								{isHealthStateUnHealthy(data.health) && (
									<MonitorRemediationSection
										data={data}
										pinnedItemsState={pinnedItemsState}
										timeRange={timeRangeSignal}
									/>
								)}
							</>
						)}

						<ScrollIndicator show={showScrollIndicator} />
					</MonitorStatusDrawerProvider>
				</ComponentBoundary>
			</section>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<DialogClose asChild>
					<LinkTo
						className="button button-primary aria-disabled:skeleton uppercase"
						disabled={data == null}
						params={{
							identifier: data?.component.identifier ?? "fallback",
						}}
						search={(search) => ({
							...search,
							query: undefined,
							explore: undefined,
						})}
						theme="unstyled"
						to={COMPONENT_PATH}
					>
						View component
					</LinkTo>
				</DialogClose>
			</footer>
		</>
	);
};

const MonitorRemediationSection: React.FC<{
	data: MonitorCheckStatus;
	pinnedItemsState: PinnedItemsState;
	timeRange: Signal<TimeRange>;
}> = ({ data, pinnedItemsState, timeRange }) => {
	const close = useDialogClose();

	if (!data.troubleshootingSteps) {
		return null;
	}

	return (
		<MonitorRemediationTabs
			componentIdentifier={data.component.identifier}
			onLinkClick={(autoPinEnabled) => {
				if (autoPinEnabled) {
					pinnedItemsState.pin({
						...data,
						component: data.component,
						timeRange,
					});
				}
				close();
			}}
			text={data.troubleshootingSteps}
			timestamp={data.topologyTime}
		/>
	);
};

const MuteButton: React.FC<{ data: MonitorCheckStatus; refresh: VoidFunction; isLiveMode: boolean }> = ({
	data,
	refresh,
	isLiveMode,
}) => {
	const label = "Silencing rules";
	const icon = <IconMuted />;

	if (!isLiveMode && data.silencingRules.length === 0) {
		return (
			<TooltipBoundary>
				<TooltipTrigger className="button button-tertiary gap-2 uppercase" disabled={true}>
					{icon} {label}
				</TooltipTrigger>
				<Tooltip className="tooltip/tooltip">
					You cannot mute a monitor when you’re time traveling. You need to go back in live mode to do this.{" "}
					<DocLink capture="noop" slug={SILENCING_DOC_LINK_SLUG}>
						Learn more
					</DocLink>
				</Tooltip>
			</TooltipBoundary>
		);
	}

	return (
		<DialogBoundary>
			<DialogTrigger className="button button-secondary gap-2 uppercase">
				{icon}
				{data.silencingRules.length > 0 ? `${label} (${data.silencingRules.length})` : label}
			</DialogTrigger>

			{isNonEmptyArray(data.silencingRules) ? (
				<UnmuteMonitorDrawer isLiveMode={isLiveMode} onDelete={refresh} silencingRules={data.silencingRules} />
			) : (
				<MuteMonitorDrawer identifier={data.identifier} onCreate={refresh} />
			)}
		</DialogBoundary>
	);
};

const ScrollIndicator: React.FC<{ show: boolean }> = ({ show }) => (
	<TooltipBoundary open={show === false ? false : undefined}>
		<TooltipTrigger
			className={cx(
				"absolute inset-x-1/2 bottom-18 z-10 flex h-18 w-10 -translate-x-1/2 flex-col items-center justify-center gap-3 rounded-[30px] bg-grey-800 text-white transition-opacity duration-500",
				show ? "opacity-100" : "opacity-0",
			)}
		>
			<div className="relative h-5.5 w-4 rounded-lg border border-white">
				<span className="absolute inset-x-1/2 top-[29%] h-1 w-1 -translate-x-1/2 animate-move rounded-full bg-white" />
			</div>
			<IconArrowDownRounded className="text-[8px]" />
		</TooltipTrigger>

		<Tooltip className="tooltip/tooltip">Scroll down</Tooltip>
	</TooltipBoundary>
);
